﻿import 'regenerator-runtime/runtime'; // Enable js async/await with parcel and babel.

import Vue from 'vue';
import VueRouter from 'vue-router';
import VueResource from 'vue-resource';
import Quasar from 'quasar';
import { QDatetimePicker, icons } from 'quasar-app-extension-qdatetimepicker/src/component';
import { QDecimal } from './../lib/quasar-app-extension-qdecimal/src/component';
import { v4 as uuidv4 } from 'uuid';
import * as Enumerable from 'linq-es2015';

import App from './application/app';
import AppRoutes from './application/app-routes';

Vue.use(VueRouter);
Vue.use(VueResource);
Vue.use(Quasar, {
    config: {
        brand: {
            primary: '#002257',
            secondary: '#2A6ED9',
            accent: '#2FCC71',

            dark: '#1D1D1D',

            positive: '#21BA45',
            negative: '#C10015',
            info: '#66B3FF',
            warning: '#F2C037'
        }
    }
});

Vue.component('q-datetime-picker', QDatetimePicker({}));
Vue.component('q-decimal', QDecimal({}));

Vue.prototype.$userPermissions = Vue.observable({ isSystemAdministrator: false });

Vue.prototype.$newGuid = function () {
    return uuidv4();
};

Vue.prototype.$joinStrings = function (...args) {
    return Enumerable.asEnumerable(...args).Where(e => e).ToArray().join(', ');
};

Vue.prototype.$generateTemporaryId = function (id) {
    if (typeof id === "number") {
        return -1;
    }

    return this.$newGuid();
};

Vue.prototype.$filterDecimalsOnKeyPress = function(value, precision, scale, allowNegative, event) 
{
    precision = precision >= 0 ? precision : 18;
    scale = scale >= 0 ? scale : -1;
    let decimalLimit = scale >= 0 ? (precision - scale) : precision;

    if (event == null && allowNegative != null && typeof allowNegative.preventDefault === 'function') {
        event = allowNegative;
        allowNegative = null;
    }

    event = (event) ? event : window.event;
    let target = event.target;
    let charCode = (event.which) ? event.which : event.keyCode;
    let isNegativeAllowed = allowNegative === true;
    let isMinusKey = charCode === 45;

    if (isMinusKey) {
        let strValue = value != null ? value.toString() : '';
        let start = target.selectionStart;
        let end = target.selectionEnd;
        let newValue = strValue.substring(0, start) + '-' + strValue.substring(end);

        if (!isNegativeAllowed || start !== 0 || (newValue.match(/-/g) || []).length > 1 || newValue[0] !== '-') {
            event.preventDefault();
            return false;
        }

        return true;
    }

    // only allow number and one dot
    if ((charCode > 31 && (charCode < 48 || charCode > 57)) && (scale === 0 || charCode !== 46)) {
        event.preventDefault();
        return false;
    }

    // Prevent multiple decimal points
    if (charCode === 46 && value != null) {
        let strValue = value.toString();
        if (strValue.indexOf('.') !== -1) {
            event.preventDefault();
            return false;
        }
    }

    // Check if the result would have more than 16 digits before decimal point
    if (value != null && target.selectionStart === target.selectionEnd) {
        let strValue = value.toString();

        // If we're adding a digit before the decimal point
        if (charCode >= 48 && charCode <= 57 && target.selectionStart <= strValue.split('.')[0].length) {
            let newValue = strValue.substring(0, target.selectionStart) +
                String.fromCharCode(charCode) +
                strValue.substring(target.selectionEnd);
            let newBeforeDecimal = newValue.split('.')[0].replace('-', '');

            if (newBeforeDecimal.length > decimalLimit) {
                event.preventDefault();
                return false;
            }
        }
    }

    // restrict to {scale} decimal places
    if (scale > 0 && value != null && target.selectionStart === target.selectionEnd) {
        let strValue = value.toString();
        let decimalPointPosition = strValue.indexOf(".");
        if (decimalPointPosition > -1 &&
            (strValue.split('.')[1].length > (scale - 1)) &&
            decimalPointPosition < target.selectionStart) {
            event.preventDefault();
            return false;
        }
    }

    return true;
};

Vue.prototype.$filterDecimalsOnPaste = function (value, precision, scale, allowNegative, event) 
{
    precision = precision >= 0 ? precision : 18;
    scale = scale >= 0 ? scale : -1;
    let decimalLimit = scale >= 0 ? (precision - scale) : precision;

    if (event == null && allowNegative != null && typeof allowNegative.preventDefault === 'function') {
        event = allowNegative;
        allowNegative = null;
    }

    event = (event) ? event : window.event;

    if (!event.clipboardData && !window.clipboardData) {
        return true;
    }

    event.preventDefault();
    const pastedData = (event.clipboardData || window.clipboardData).getData('text');
    const isNegativeAllowed = allowNegative === true;

    // Remove any non-numeric characters except decimal point and an optional leading minus sign
    let cleanedData = pastedData.replace(/[^\d.-]/g, '');

    // Ensure only one decimal point
    const parts = cleanedData.split('.');
    if (parts.length > 2) {
        return false;
    }

    const minusCount = (cleanedData.match(/-/g) || []).length;
    if (minusCount > 1) {
        return false;
    }

    if (minusCount === 1) {
        if (!isNegativeAllowed || cleanedData[0] !== '-') {
            return false;
        }
    }

    // Check if the number of digits before decimal point exceeds the configured limit
    const beforeDecimal = parts[0].replace('-', '');
    if (beforeDecimal.length > decimalLimit) {
        return false;
    }

    // Check decimal scale
    if (scale > 0 && parts[1] && parts[1].length > scale) {
        return false;
    }

    // If valid, manually insert the cleaned value
    let newValue = cleanedData;
    const target = event.target;
    const currentValue = (value || '').toString();
    if(currentValue.length > 0) {
        const start = target.selectionStart;
        const end = target.selectionEnd;
        newValue = currentValue.substring(0, start) + cleanedData + currentValue.substring(end);
    }

    // Final validation of the complete value
    const finalParts = newValue.split('.');
    if (finalParts.length > 2) {
        return false;
    }

    const finalMinusCount = (newValue.match(/-/g) || []).length;
    if (finalMinusCount > 1) {
        return false;
    }

    if (finalMinusCount === 1) {
        if (!isNegativeAllowed || newValue[0] !== '-') {
            return false;
        }
    }

    if (scale > 0 && finalParts[1] && finalParts[1].length > scale) {
        return false;
    }

    // Check if the final value has more than the allowed digits before decimal point
    const finalBeforeDecimal = finalParts[0].replace('-', '');
    if (finalBeforeDecimal.length > decimalLimit) {
        return false;
    }

    // Set the value and trigger Vue's reactivity
    target.value = newValue;
    target.dispatchEvent(new Event('input', { bubbles: true }));

    return true;
};

Vue.prototype.$prepareDialogModel = function (modelObject) {
    let dialogModel = {};

    if (modelObject) {
        for (var propertyName in modelObject) {
            if (propertyName.toUpperCase() != 'ID' && propertyName.toUpperCase().includes('ID')) {
                dialogModel[propertyName] = modelObject[propertyName];
            }
        }
    }

    return dialogModel;
};

Vue.prototype.$applyModelFields = function (modelSource, modelDestination) {
    if (modelDestination && modelSource) {
        for (var propertyName in modelSource) {
            if (propertyName.toUpperCase() != 'ID' && modelDestination.hasOwnProperty(propertyName)) {
                modelDestination[propertyName] = modelSource[propertyName];
            }
        }
    }
};

Vue.prototype.$prepareServerUrl = function (url, id, params, routeParams) {

    let preparedUrl = url
        .replace('{id}', id)
        .replace('{id?}', id);

    if (params) {
        for (let param in params) {
            preparedUrl = preparedUrl.replace(new RegExp('{' + param + '}', "ig"), params[param]);
        }
    }

    if (routeParams) {
        for (let routeParam in routeParams) {
            preparedUrl = preparedUrl.replace(new RegExp('{' + routeParam + '}', "ig"), routeParams[routeParam]);
        }
    }

    return preparedUrl;
};

Vue.prototype.$prepareNavigationUrl = function (url, id, params, routeParams) {

    let preparedUrl = url
        .replace(':id', id);

    if (params) {
        for (let param in params) {
            preparedUrl = preparedUrl.replace(new RegExp(':' + param, "ig"), params[param]);
        }
    }

    if (routeParams) {
        for (let routeParam in routeParams) {
            preparedUrl = preparedUrl.replace(new RegExp(':' + routeParam, "ig"), routeParams[routeParam]);
        }
    }

    return preparedUrl;
};

Vue.prototype.$showNotification = function (type, message) {
    this.$q.notify({
        position: 'top-right',
        type: type,
        message: message
    });
};

Vue.prototype.$processException = function (exception) {
    if (exception.ok === false) {
        if (exception.status === 403) {
            if (exception.body.message) {
                this.$q.dialog({
                    title: 'Forbidden',
                    message: exception.body.message.replaceAll('\r\n', '<br />'),
                    html: true
                });
            }

            this.$router.replace({ path: '/Forbidden' });
            return;
        }

        if (exception.status === 404) {
            this.$router.replace({ path: '/NotFound' });
            return;
        }

        let message = exception.data.message;

        if (exception.status === 500) {
            message = message + ' Trace ID: ' + exception.data.traceId;
        }

        this.$q.dialog({
            title: 'Error',
            message: message.replaceAll('\r\n', '<br />'),
            html: true
        });

        return;
    }
};

Vue.prototype.$processServerJsonResponse = function (response, refreshScreenAction) {
    // Check if resposne contains ServerActionResult model.
    let responseType = undefined;
    if (response.hasOwnProperty('type') === true)
        responseType = response.type;
    else if (response.hasOwnProperty('serverActionType') === true)
        responseType = response.serverActionType;
    else
        return false;

    if (response.success === false) {
        var msg = response.message ? response.message : 'Something went wrong';

        if (response.popup) {
            this.$showNotification(response.popup, msg);
        } else {
            this.$q.dialog({
                title: 'Failure',
                message: msg.replaceAll('\r\n', '<br />'),
                html: true
            });
        }

        return true;
    }

    // ServerActionType.RefreshScreen.
    if (responseType === 1 && refreshScreenAction) {
        this.$showServerResultMessage(response, (response) => {
            refreshScreenAction();
        });

        return true;
    }

    // ServerActionType.NavigateToUrl.
    if (responseType === 2 && response.NavigateToUrl && response.NavigateToUrl !== '') {
        this.$showServerResultMessage(response, (response) => {
            if (this.$router.currentRoute.path == response.NavigateToUrl) {
                // Refresh the current page.
                this.$router.go();
            } else {
                // Route to the page.
                this.$router.push(response.NavigateToUrl);
            }
        });

        return true;
    }

    // ServerActionType.RedirectToUrl.
    if (responseType === 3 && response.RedirectToUrl && response.RedirectToUrl !== '') {
        this.$showServerResultMessage(response, (response) => {
            if (response.InNewWindow) {
                window.open(response.RedirectToUrl, '_blank');
            } else {
                window.location.href = response.RedirectToUrl;
            }
        });

        return true;
    }

    var msg = response.message ? response.message : 'Success';

    if (response.popup) {
        this.$showNotification(response.popup, msg);
    } else {
        this.$q.dialog({
            title: 'Success',
            message: msg.replaceAll('\r\n', '<br />'),
            html: true
        });
    }

    return true;
};

Vue.prototype.$showServerResultMessage = function (response, callback) {
    if (response.message) {
        if (response.popup) {
            if (callback) {
                callback(response);
            }

            // Show message as popup notification.
            this.$showNotification(response.popup, response.message);

            return;
        }
        else {
            // Show message as dialog.
            this.$q.dialog({
                title: 'Success',
                message: response.message.replaceAll('\r\n', '<br />'),
                html: true
            }).onOk(() => {
                if (callback) {
                    callback(response);
                }

                return;
            });
        }
    }

    if (callback) {
        callback(response);
    }
};

Vue.prototype.$processServerFileResponse = function (response) {
    let contentType = response.headers.get('content-type');
    let contentDisposition = response.headers.get('content-disposition') || '';

    let blob = new Blob([response.data], { type: contentType });

    // Parse filename from Content-Disposition header
    // Handles both: filename="file.csv" and filename*=UTF-8''file.csv formats
    let filename = '';
    if (contentDisposition) {
        // Try filename* first (RFC 5987 format)
        const filenameStarMatch = contentDisposition.match(/filename\*=([^;]+)/i);
        if (filenameStarMatch) {
            const value = filenameStarMatch[1].trim();
            // Format: UTF-8''encoded-filename or charset''encoded-filename
            const parts = value.split("''");
            if (parts.length > 1) {
                filename = decodeURIComponent(parts[1]);
            } else {
                filename = decodeURIComponent(value);
            }
        } else {
            // Try regular filename parameter
            const filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
            if (filenameMatch) {
                filename = filenameMatch[1].trim();
                // Remove quotes if present
                if ((filename.startsWith('"') && filename.endsWith('"')) ||
                    (filename.startsWith("'") && filename.endsWith("'"))) {
                    filename = filename.slice(1, -1);
                }
            }
        }
    }

    let result = document.createElement('a');
    result.href = window.URL.createObjectURL(blob);
    result.download = filename;
    result.click();
};

new Vue({
    router: new VueRouter({
        routes: AppRoutes
    }),
    template: '<App />',
    components: { App }
}).$mount('#app');
